<template>
  <v-container fluid class="pa-0">
    <v-card outlined>
      <v-card-text class="pa-4">

        <!-- Loading -->
        <div v-if="loading" class="text-center py-12">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
          ></v-progress-circular>
          <div class="mt-4 grey--text text--darken-1">
            Loading rent roll data...
          </div>
        </div>

        <template v-else>
          <!-- Month Selection - Grouped by Year with Accordion -->
          <v-row v-if="availableMonths.length" class="mt-2 mb-4">
            <v-col cols="12">
              <v-card outlined class="pa-2" style="background: #fafafa;">
                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                  <span class="font-weight-medium caption mr-2">Months:</span>
                  <v-btn small text color="primary" class="caption" @click="selectAllMonths">All</v-btn>
                  <v-btn small text color="error" class="caption" @click="clearAllMonths">Clear</v-btn>
                  <v-chip small color="primary" text-color="white" class="caption">
                    {{ selectedMonths.length }}
                  </v-chip>
                  <span class="caption grey--text text--darken-1 ml-2">
                    {{ selectedMonthsText }}
                  </span>
                </div>
                
                <!-- Accordion grouped by year -->
                <v-expansion-panels v-model="expandedYears" multiple class="mt-2">
                  <v-expansion-panel v-for="yearGroup in groupedMonths" :key="yearGroup.year">
                    <v-expansion-panel-header class="caption pa-2">
                      <div class="d-flex align-center">
                        <span class="font-weight-bold">{{ yearGroup.year }}</span>
                        <v-chip small class="ml-2 caption" color="grey lighten-2">
                          {{ yearGroup.months.length }} months
                        </v-chip>
                        <v-chip small class="ml-2 caption" color="primary" text-color="white" v-if="yearGroup.selectedCount">
                          {{ yearGroup.selectedCount }} selected
                        </v-chip>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div class="month-checkbox-grid pa-2">
                        <v-checkbox
                          v-for="m in yearGroup.months"
                          :key="m.key"
                          v-model="selectedMonths"
                          :label="m.label"
                          :value="m.key"
                          hide-details
                          dense
                          class="month-checkbox caption"
                        />
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card>
            </v-col>
          </v-row>

          <template v-if="flatData?.length">
            <!-- Filters + Export - Compact -->
            <v-row class="mb-4 mt-2" align="center">
              <v-col cols="12" md="8">
                <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                  <v-select
                    v-model="selectedUnit"
                    :items="unitOptions"
                    label="All Units"
                    outlined
                    dense
                    hide-details
                    clearable
                    style="max-width: 180px;"
                    class="caption"
                  />

                  <v-btn-toggle v-model="sortMode" mandatory dense small>
                    <v-btn small value="reference" class="caption">Ref</v-btn>
                    <v-btn small value="unit" class="caption">Unit</v-btn>
                  </v-btn-toggle>
                </div>
              </v-col>

              <v-col cols="12" md="4" class="text-right">
                <v-btn
                  color="error"
                  dark
                  small
                  @click="generatePdf"
                  :loading="generatingPdf"
                  :disabled="!filteredFlatData.length"
                  class="caption"
                >
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  Export PDF
                </v-btn>
              </v-col>
            </v-row>

            <!-- Printable content -->
            <div id="print-content">
              <div class="text-center mb-6">
                <img
                  v-if="base64log"
                  :src="base64log"
                  style="max-height:100px; max-width:100px; object-fit: contain;"
                >
                <h4 class="mt-3 mb-1">{{ report.title }}</h4>
                <p v-if="selectedMonths.length" class="caption mb-0">
                  <strong>Months:</strong> {{ selectedMonthsText }}
                </p>
                <hr style="margin:16px 0; border-color:#ddd;">
              </div>

              <div class="table-responsive">
                <table class="spreadsheet-table">
                  <thead>
                    <tr>
                      <th class="text-left" style="min-width: 100px;">REFERENCE</th>
                      <th class="text-left" style="min-width: 200px;">PROPERTY - UNIT - TENANT</th>
                      <th class="text-left" style="min-width: 130px;">MONTH</th>
                      <th
                        v-for="col in visibleColumns"
                        :key="col.field"
                        :class="col.align === 'right' ? 'text-right' : 'text-left'"
                      >
                        {{ col.header }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in filteredFlatData" :key="row.id">
                      <td class="text-left font-weight-medium">
                        {{ row.reference }}
                      </td>
                      <td class="text-left">
                        {{ formatPropertyUnitTenant(row) }}
                      </td>
                      <td class="text-left font-weight-medium">
                        {{ row.month }}
                      </td>
                      <td
                        v-for="col in visibleColumns"
                        :key="col.field"
                        :class="getCellClass(row, col)"
                      >
                        {{ formatCell(row, col.field) }}
                      </td>
                    </tr>

                    <!-- Grand Total Row -->
                    <tr class="font-weight-bold blue lighten-5">
                      <td colspan="3" class="text-left pl-4">TOTAL</td>
                      <td
                        v-for="(col, i) in visibleColumns"
                        :key="`total-${col.field}-${i}`"
                        class="text-right"
                      >
                        {{ formatCurrency(grandTotals[col.field]) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>

          <div v-else class="text-center py-12 grey--text caption">
            No transactions found for the selected properties / units
          </div>
        </template>

      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const db = getFirestore();

export default {
  name: "RentRollDynamicColumns",

  props: {
    property: { type: Object, default: null },
    properties: { type: Array, default: () => [] },
    unit: { type: Object, default: null },
    landlord: { type: Object, default: null }
  },

  data() {
    return {
      report: {
        title: 'Rent Roll'
      },
      base64log: "",
      flatData: [],
      loading: false,
      generatingPdf: false,
      sortMode: 'unit',
      selectedUnit: null,
      selectedMonths: [],
      expandedYears: [],
      unitOptions: [{ text: 'All Units', value: null }],
    };
  },

  computed: {
    allColumns() {
      return [
        { field: 'rentCharged', header: 'Rent', alwaysVisible: true, align: 'right' },
        { field: 'waterCharged', header: 'Water', align: 'right' },
        { field: 'waterAv', header: 'W Av', align: 'right' },
        { field: 'sewerageCharged', header: 'Sewerage', align: 'right' },
        { field: 'sewerageAv', header: 'Sew Av', align: 'right' },
        { field: 'refuseCharged', header: 'Refuse', align: 'right' },
        { field: 'adminCharged', header: 'Admin', align: 'right' },
        { field: 'parkingCharged', header: 'Parking', align: 'right' },
        { field: 'bankFees', header: 'Bank Fees', align: 'right' },
        { field: 'leaseFees', header: 'Lease Fees', align: 'right' },
        { field: 'insurance', header: 'Insurance', align: 'right' },
        { field: 'legalExpenses', header: 'Legal', align: 'right' },
        { field: 'repairsMaintenance', header: 'Repairs', align: 'right' },
        { field: 'utilities', header: 'Utilities', align: 'right' },
        { field: 'otherVendorExpenses', header: 'Vendor Exp', align: 'right' },
        { field: 'otherCharge', header: 'Other', align: 'right' },
        { field: 'subTotal', header: 'Sub Total', alwaysVisible: true, align: 'right' },
        { field: 'bankReceived', header: 'Received', alwaysVisible: true, align: 'right' },
        { field: 'balance', header: 'Balance', alwaysVisible: true, align: 'right' }
      ];
    },

    visibleColumns() {
      return this.allColumns.filter(col => {
        if (col.alwaysVisible) return true;
        const total = Number(this.grandTotals[col.field] || 0);
        return Math.abs(total) > 0.005;
      });
    },

    grandTotals() {
      const totals = {};
      this.allColumns.forEach(col => {
        totals[col.field] = 0;
      });

      this.filteredFlatData.forEach(row => {
        this.allColumns.forEach(col => {
          totals[col.field] += Number(row[col.field] || 0);
        });
      });

      return totals;
    },

    availableMonths() {
      if (!this.flatData.length) return [];
      const months = new Set(this.flatData.map(r => r.month));
      const sorted = [...months].sort((a, b) => {
        const [ma, ya] = a.split(' ');
        const [mb, yb] = b.split(' ');
        const order = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return yb - ya || order.indexOf(mb) - order.indexOf(ma);
      });
      return sorted.map(m => ({ key: m, label: m }));
    },

    groupedMonths() {
      const groups = {};
      this.availableMonths.forEach(month => {
        const year = month.key.split(' ')[1];
        if (!groups[year]) {
          groups[year] = { year, months: [], selectedCount: 0 };
        }
        groups[year].months.push(month);
        if (this.selectedMonths.includes(month.key)) {
          groups[year].selectedCount++;
        }
      });
      return Object.values(groups).sort((a, b) => b.year - a.year);
    },

    selectedMonthsText() {
      if (!this.selectedMonths || this.selectedMonths.length === 0) {
        return 'No months selected';
      }
      return this.selectedMonths.join(', ');
    },

    filteredFlatData() {
      let data = [...this.flatData];

      if (this.selectedMonths && this.selectedMonths.length > 0) {
        data = data.filter(r => this.selectedMonths.includes(r.month));
      }

      if (this.selectedUnit) {
        data = data.filter(r => r.unit === this.selectedUnit);
      }

      if (this.sortMode === 'unit') {
        data.sort((a, b) => Number(a.unit || 999999) - Number(b.unit || 999999));
      } else {
        data.sort((a, b) => a.reference.localeCompare(b.reference));
      }

      return data;
    }
  },

  watch: {
    properties: {
      handler() {
        if (this.properties?.length || this.property) this.loadRentRollData();
      },
      deep: true,
      immediate: true
    },
    flatData: {
      handler() {
        if (!this.availableMonths.length || this.selectedMonths.length > 0) return;
        
        // Default: ONLY select the current month
        const now = new Date();
        const currentMonth = now.toLocaleString('default', { month: 'long' }) + ' ' + now.getFullYear();
        const months = this.availableMonths;
        
        // Find current month
        const currentMonthData = months.find(m => m.key === currentMonth);
        
        // If current month exists in the data, select it
        if (currentMonthData) {
          this.selectedMonths = [currentMonthData.key];
        } else if (months.length > 0) {
          // If current month not found, select the most recent month available
          this.selectedMonths = [months[0].key];
        }
        
        // Auto-expand years that have selected months
        this.expandedYears = this.groupedMonths
          .filter(group => group.months.some(m => this.selectedMonths.includes(m.key)))
          .map(group => this.groupedMonths.indexOf(group));
      },
      immediate: true
    }
  },

  async created() {
    await this.fetchLogoBase64();
  },

  methods: {
    selectAllMonths() {
      this.selectedMonths = this.availableMonths.map(m => m.key);
      this.expandedYears = this.groupedMonths.map((_, index) => index);
    },
    clearAllMonths() {
      this.selectedMonths = [];
      this.expandedYears = [];
    },
    async fetchLogoBase64() {
      try {
        const logosRef = collection(db, 'Logo');
        const q = query(logosRef, where('id', '==', 2));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          let logoStr = snapshot.docs[0].data().logo2 || '';
          logoStr = logoStr.trim().replace(/^data:image\/[a-z]+;base64,/, '');
          this.base64log = `data:image/png;base64,${logoStr}`;
        }
      } catch (err) {
        console.error('Logo fetch failed:', err);
      }
    },

    async loadRentRollData() {
      this.loading = true;
      try {
        const props = this.properties.length ? this.properties : this.property ? [this.property] : [];
        if (!props.length) {
          this.flatData = [];
          return;
        }

        const flat = [];

        for (const prop of props) {
          let q = query(collection(db, "Leases"), where("propertyId", "==", prop.id));
          if (this.unit?.unitName) q = query(q, where("address", "==", this.unit.unitName));

          const leasesSnap = await getDocs(q);

          for (const leaseDoc of leasesSnap.docs) {
            const d = leaseDoc.data();
            const ref = d.leaseReference;
            const tenant = d.tenantName || 'Unknown Tenant';
            const unit = d.address?.includes("Unit") ? d.address.split("Unit")[1]?.trim() : "";

            const txQ = query(
              collection(db, "Transactions"),
              where("leaseReference", "==", ref),
              orderBy("datePaid", "asc")
            );
            const txSnap = await getDocs(txQ);

            const months = {};

            txSnap.docs.forEach(doc => {
              const t = doc.data();
              const date = new Date(t.datePaid);
              const monthKey = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;

              months[monthKey] = months[monthKey] || {
                rentCharged: 0, waterCharged: 0, waterAv: 0, sewerageCharged: 0, sewerageAv: 0,
                refuseCharged: 0, adminCharged: 0, parkingCharged: 0, bankFees: 0, leaseFees: 0,
                insurance: 0, legalExpenses: 0, repairsMaintenance: 0, utilities: 0,
                otherVendorExpenses: 0, otherCharge: 0,
                subTotal: 0, bankReceived: 0, balance: 0
              };

              const m = months[monthKey];
              const amt = parseFloat(t.amountPaid) || 0;
              if (amt === 0) return;

              const desc = (t.description || '').toLowerCase().trim();

              if (t.type === "payment") {
                m.bankReceived += Math.abs(amt);
              } else {
                if (/rent|monthly rent/i.test(desc)) {
                  m.rentCharged += amt;
                } else if (/water/i.test(desc) && !/av|availability/i.test(desc)) {
                  m.waterCharged += amt;
                } else if (/water.*av|availability/i.test(desc)) {
                  m.waterAv += amt;
                } else if (/sewerage/i.test(desc) && !/av|availability/i.test(desc)) {
                  m.sewerageCharged += amt;
                } else if (/sewerage.*av|availability/i.test(desc)) {
                  m.sewerageAv += amt;
                } else if (/refuse|garbage|rubbish/i.test(desc)) {
                  m.refuseCharged += amt;
                } else if (/admin|administration|levy|service fee/i.test(desc)) {
                  m.adminCharged += amt;
                } else if (/parking/i.test(desc)) {
                  m.parkingCharged += amt;
                } else if (/bank.*fee|bank charge|debit order fee/i.test(desc)) {
                  m.bankFees += amt;
                } else if (/lease.*fee|lease registration|lease renewal/i.test(desc)) {
                  m.leaseFees += amt;
                } else if (/insurance/i.test(desc)) {
                  m.insurance += amt;
                } else if (/legal|attorney|lawyer|eviction|court/i.test(desc)) {
                  m.legalExpenses += amt;
                } else if (/repair|maintenance|fix|plumb|electri|paint|build/i.test(desc)) {
                  m.repairsMaintenance += amt;
                } else if (/utilit(y|ies)|electricity|gas|power/i.test(desc)) {
                  m.utilities += amt;
                } else if (/vendor|supplier|contractor|service provider/i.test(desc)) {
                  m.otherVendorExpenses += amt;
                } else {
                  m.otherCharge += amt;
                }
              }
            });

            Object.keys(months).forEach(key => {
              const m = months[key];

              m.subTotal = 
                m.rentCharged + m.waterCharged + m.waterAv + m.sewerageCharged + m.sewerageAv +
                m.refuseCharged + m.adminCharged + m.parkingCharged + m.bankFees + m.leaseFees +
                m.insurance + m.legalExpenses + m.repairsMaintenance + m.utilities +
                m.otherVendorExpenses + m.otherCharge;

              m.balance = m.subTotal - m.bankReceived;

              flat.push({
                id: `${ref}-${key}`,
                month: key,
                reference: ref,
                property: prop.displayName || 'Property',
                unit,
                tenantName: tenant,
                rentCharged: m.rentCharged,
                waterCharged: m.waterCharged,
                waterAv: m.waterAv,
                sewerageCharged: m.sewerageCharged,
                sewerageAv: m.sewerageAv,
                refuseCharged: m.refuseCharged,
                adminCharged: m.adminCharged,
                parkingCharged: m.parkingCharged,
                bankFees: m.bankFees,
                leaseFees: m.leaseFees,
                insurance: m.insurance,
                legalExpenses: m.legalExpenses,
                repairsMaintenance: m.repairsMaintenance,
                utilities: m.utilities,
                otherVendorExpenses: m.otherVendorExpenses,
                otherCharge: m.otherCharge,
                subTotal: m.subTotal,
                bankReceived: m.bankReceived,
                balance: m.balance
              });
            });
          }
        }

        this.flatData = flat;
        this.extractUniqueUnits();
      } catch (err) {
        console.error("Error loading rent roll data:", err);
      } finally {
        this.loading = false;
      }
    },

    extractUniqueUnits() {
      const units = new Set(this.flatData.map(r => r.unit).filter(Boolean));
      this.unitOptions = [
        { text: 'All Units', value: null },
        ...[...units].sort((a,b) => Number(a)-Number(b)).map(u => ({text:`Unit ${u}`, value:u}))
      ];
    },

    formatPropertyUnitTenant(row) {
      // Format: "Property - Unit X - Tenant" or "Property - Tenant" if no unit
      const property = row.property || 'Unknown Property';
      const tenant = row.tenantName || 'Unknown';
      if (row.unit && row.unit.trim() !== '') {
        return `${property} - Unit ${row.unit} - ${tenant}`;
      } else {
        return `${property} - ${tenant}`;
      }
    },

    formatCell(row, field) {
      return this.formatCurrency(row[field]);
    },

    getCellClass(row, col) {
      const val = Number(row[col.field] || 0);

      if (col.field === 'balance') {
        if (val < 0) return 'error--text text-right';
        if (val > 0) return 'warning--text text-right';
        return 'text-right';
      }

      if (val < 0) return 'error--text text-right';
      return 'text-right';
    },

    formatCurrency(v) {
      if (v == null || isNaN(v) || Number(v) === 0) return '—';

      const num = Number(v);
      const abs = Math.abs(num);
      const sign = num < 0 ? '-' : '';

      const parts = abs.toFixed(2).split('.');
      const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const decimal = parts[1];

      return `R ${sign}${integer},${decimal}`;
    },

    async generatePdf() {
      if (!this.filteredFlatData?.length) return;

      this.generatingPdf = true;

      try {
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 40;
        let y = 35;

        // Header
        if (this.base64log) {
          try {
            const logoSrc = this.base64log.startsWith('data:')
              ? this.base64log
              : `data:image/png;base64,${this.base64log}`;

            const logoWidth = 80;
            const logoHeight = 25;
            const logoX = (pageWidth - logoWidth) / 2;

            doc.addImage(logoSrc, 'PNG', logoX, y, logoWidth, logoHeight);
            y += logoHeight + 15;
          } catch (e) {
            console.warn('Logo failed in PDF', e);
            y += 20;
          }
        }

        let headerTitle = "Rent Roll";

        if (this.properties?.length > 1) {
          headerTitle += " - Multiple Properties";
        } else if (this.property?.displayName) {
          headerTitle += ` - ${this.property.displayName}`;
        } else if (this.landlord?.name) {
          headerTitle += ` - Landlord: ${this.landlord.name}`;
        }

        doc.setFontSize(16);
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "bold");
        doc.text(headerTitle, pageWidth / 2, y, { align: 'center' });
        y += 22;

        // Show selected months
        const monthLabel = this.selectedMonthsText || '';
        if (monthLabel) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(85, 85, 85);
          doc.text(`Months: ${monthLabel}`, pageWidth / 2, y, { align: 'center' });
          y += 16;
        }

        doc.setLineWidth(0.7);
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, y, pageWidth - margin, y);
        y += 20;

        // Table - All columns including REFERENCE and PROPERTY - UNIT - TENANT
        const tableColumns = [
          { header: 'REFERENCE', dataKey: 'reference' },
          { header: 'PROPERTY - UNIT - TENANT', dataKey: 'propertyUnitTenant' },
          { header: 'MONTH', dataKey: 'month' },
          ...this.visibleColumns.map(col => ({
            header: col.header,
            dataKey: col.field
          }))
        ];

        const tableBody = this.filteredFlatData.map(row => {
          const rowObj = {
            reference: row.reference || '',
            propertyUnitTenant: this.formatPropertyUnitTenant(row),
            month: row.month
          };

          this.visibleColumns.forEach(col => {
            rowObj[col.field] = this.formatCurrency(Number(row[col.field] || 0));
          });

          return rowObj;
        });

        // Totals row
        const totalsRow = { 
          reference: 'TOTAL', 
          propertyUnitTenant: '', 
          month: '' 
        };
        this.visibleColumns.forEach(col => {
          totalsRow[col.field] = this.formatCurrency(Number(this.grandTotals[col.field] || 0));
        });
        tableBody.push(totalsRow);

        autoTable(doc, {
          columns: tableColumns,
          body: tableBody,
          startY: y,
          theme: 'grid',
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 7.5,
            cellPadding: 4,
            lineColor: [180, 180, 180],
            lineWidth: 0.3,
            overflow: 'linebreak'
          },
          headStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 7
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          columnStyles: {
            reference: { cellWidth: 70, halign: 'left' },
            propertyUnitTenant: { cellWidth: 160, halign: 'left' },
            month: { cellWidth: 80, halign: 'left', fontStyle: 'bold' }
          },
          didParseCell: (data) => {
            if (data.column.dataKey !== 'reference' && 
                data.column.dataKey !== 'propertyUnitTenant' && 
                data.column.dataKey !== 'month') {
              data.cell.styles.halign = 'right';
            }
            if (data.row.index === tableBody.length - 1) {
              data.cell.styles.fillColor = [210, 230, 245];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          didDrawPage: () => {
            doc.setFontSize(6);
            doc.setTextColor(120);
            doc.text(
              `Page ${doc.internal.getNumberOfPages()} • ${new Date().toLocaleDateString('en-ZA')}`,
              pageWidth - margin - 10,
              doc.internal.pageSize.height - 15,
              { align: 'right' }
            );
          }
        });

        const safeMonth = (this.selectedMonths.join('_') || 'All_Months').replace(/[^a-zA-Z0-9_]/g, '_');

        let filePrefix = 'Rent_Roll';
        if (this.properties?.length > 1) {
          filePrefix = 'Rent_Roll_Multiple';
        } else if (this.property?.displayName) {
          filePrefix = `Rent_Roll_${this.property.displayName.replace(/[^a-zA-Z0-9]/g, '_')}`;
        } else if (this.landlord?.name) {
          filePrefix = `Rent_Roll_${this.landlord.name.replace(/[^a-zA-Z0-9]/g, '_')}`;
        }

        doc.save(`${filePrefix}_${safeMonth}_${new Date().toISOString().slice(0,10)}.pdf`);

      } catch (err) {
        console.error('PDF generation failed:', err);
        alert('Failed to generate PDF. Please check the console for details.');
      } finally {
        this.generatingPdf = false;
      }
    }
  }
};
</script>

<style scoped>
.spreadsheet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem !important;
  table-layout: auto;
}

.spreadsheet-table th,
.spreadsheet-table td {
  border: 1px solid #bbb;
  padding: 5px 6px !important;
  font-size: 0.65rem !important;
}

.spreadsheet-table th {
  background-color: #f0f0f0;
  font-weight: 600;
  white-space: nowrap;
  font-size: 0.6rem !important;
}

.spreadsheet-table td:first-child {
  font-weight: 500;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.error--text { color: #d32f2f !important; }
.warning--text { color: #f57c00 !important; }

/* Month checkbox grid - compact */
.month-checkbox-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
  padding: 2px 0;
}

.month-checkbox {
  flex: 0 0 auto;
  min-width: 100px;
  margin: 0;
}

.month-checkbox >>> .v-label {
  font-size: 0.7rem !important;
  font-weight: 400;
}

.month-checkbox >>> .v-input--selection-controls__input {
  margin-right: 4px !important;
}

/* Accordion styles */
.v-expansion-panel {
  border: 1px solid #e0e0e0;
  border-radius: 4px !important;
  margin-bottom: 4px;
}

.v-expansion-panel:last-child {
  margin-bottom: 0;
}

.v-expansion-panel-header {
  min-height: 36px !important;
  padding: 0 12px !important;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 4px 8px !important;
}

/* Compact form elements */
.caption {
  font-size: 0.7rem !important;
}

.v-btn.caption {
  font-size: 0.65rem !important;
  letter-spacing: 0.3px;
  min-width: 32px;
  padding: 0 8px;
  height: 24px;
}

.v-chip.caption {
  font-size: 0.6rem !important;
  height: 20px !important;
}

.v-select.caption >>> .v-input__slot {
  min-height: 32px !important;
}

.v-select.caption >>> .v-select__selection {
  font-size: 0.7rem !important;
}

/* Print styles */
@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
  .spreadsheet-table {
    font-size: 0.55rem !important;
  }
  .spreadsheet-table th,
  .spreadsheet-table td {
    padding: 3px 4px !important;
    font-size: 0.5rem !important;
  }
}
</style>